/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

export enum EnvironmentTypes {
  DEVELOPMENT = 'DEVELOPMENT',
  STAGING = 'STAGING',
  PRODUCTION = 'PRODUCTION',
  PRE_PRODUCTION = 'PRE_PRODUCTION',
}

export enum AccountEndpoints {
  LIFE_API_BASE_URL = 'LIFE_API_BASE_URL',
  PAYMENT_API_BASE_URL = 'PAYMENT_API_BASE_URL',
  BIN_SETTINGS_BASE_URL = 'BIN_SETTINGS_BASE_URL',
  SSO_WIDGET_BASE_URL = 'SSO_WIDGET_BASE_URL',
  FAVOURITE_API_BASE_URL = 'FAVOURITE_API_BASE_URL',
  BASE_ACCOUNT_URL = 'BASE_ACCOUNT_URL',
  FAQ_BASE_URL = 'FAQ_BASE_URL',
  CONTENT_API_BASE_URL = 'CONTENT_API_BASE_URL',
  ASKBO_CORE_SERVICE_BASE_URL = 'ASKBO_CORE_SERVICE_BASE_URL',
  CARD_VAULT_API_BASE_URL = 'CARD_VAULT_API_BASE_URL',
  ACCOUNT_WEB_BASE_URL = 'ACCOUNT_WEB_BASE_URL',
  MY_CASES_WIDGET_SCRIPT_URL = 'MY_CASES_WIDGET_SCRIPT_URL',
}

type EnvironmentURLMap = Record<EnvironmentTypes, string>;

const AccountEnvironmentURLs: Record<AccountEndpoints, EnvironmentURLMap> = {
  [AccountEndpoints.LIFE_API_BASE_URL]: {
    [EnvironmentTypes.DEVELOPMENT]: 'https://life.stgairasia.com',
    [EnvironmentTypes.STAGING]: 'https://life.stgairasia.com',
    [EnvironmentTypes.PRODUCTION]: 'https://life.airasia.com',
    [EnvironmentTypes.PRE_PRODUCTION]: 'https://life.airasia.com',
  },
  [AccountEndpoints.PAYMENT_API_BASE_URL]: {
    [EnvironmentTypes.DEVELOPMENT]: 'https://payment-api.stgairasia.com',
    [EnvironmentTypes.STAGING]: 'https://payment-api.stgairasia.com',
    [EnvironmentTypes.PRODUCTION]: 'https://payment-api.airasia.com',
    [EnvironmentTypes.PRE_PRODUCTION]: 'https://payment-api.airasia.com',
  },
  [AccountEndpoints.BIN_SETTINGS_BASE_URL]: {
    [EnvironmentTypes.DEVELOPMENT]:
      'https://payment.stg-apiairasia.com/stgpayment',
    [EnvironmentTypes.STAGING]: 'https://payment.stg-apiairasia.com/stgpayment',
    [EnvironmentTypes.PRODUCTION]: 'https://payment.apiairasia.com/payment',
    [EnvironmentTypes.PRE_PRODUCTION]: 'https://payment.apiairasia.com/payment',
  },
  [AccountEndpoints.SSO_WIDGET_BASE_URL]: {
    [EnvironmentTypes.DEVELOPMENT]: 'https://sso-widget.devairasia.com',
    [EnvironmentTypes.STAGING]: 'https://sso-widget.stgairasia.com',
    [EnvironmentTypes.PRODUCTION]: 'https://sso-widget.airasia.com',
    [EnvironmentTypes.PRE_PRODUCTION]: 'https://sso-widget.airasia.com',
  },
  [AccountEndpoints.FAVOURITE_API_BASE_URL]: {
    [EnvironmentTypes.DEVELOPMENT]: 'https://social.stgairasia.com',
    [EnvironmentTypes.STAGING]: 'https://social.stgairasia.com',
    [EnvironmentTypes.PRODUCTION]: 'https://social.airasia.com',
    [EnvironmentTypes.PRE_PRODUCTION]: 'https://social.airasia.com',
  },
  [AccountEndpoints.BASE_ACCOUNT_URL]: {
    [EnvironmentTypes.DEVELOPMENT]: 'https://www.stgairasia.com',
    [EnvironmentTypes.STAGING]: 'https://www.stgairasia.com',
    [EnvironmentTypes.PRODUCTION]: 'https://www.airasia.com',
    [EnvironmentTypes.PRE_PRODUCTION]: 'https://www.airasia.com',
  },
  [AccountEndpoints.FAQ_BASE_URL]: {
    [EnvironmentTypes.DEVELOPMENT]:
      'https://aacsm--aauat.sandbox.my.site.com/s/',
    [EnvironmentTypes.STAGING]: 'https://aacsm--aauat.sandbox.my.site.com/s/',
    [EnvironmentTypes.PRODUCTION]: 'https://support.airasia.com/s/',
    [EnvironmentTypes.PRE_PRODUCTION]: 'https://support.airasia.com/s/',
  },
  [AccountEndpoints.CONTENT_API_BASE_URL]: {
    [EnvironmentTypes.DEVELOPMENT]: 'https://cs-next.stgairasia.com',
    [EnvironmentTypes.STAGING]: 'https://cs-next.stgairasia.com',
    [EnvironmentTypes.PRODUCTION]: 'https://cs-next.airasia.com',
    [EnvironmentTypes.PRE_PRODUCTION]: 'https://cs-next.stgairasia.com',
  },
  [AccountEndpoints.ASKBO_CORE_SERVICE_BASE_URL]: {
    [EnvironmentTypes.DEVELOPMENT]:
      'https://askbo-core-service-399143297583.asia-southeast1.run.app',
    [EnvironmentTypes.STAGING]:
      'https://askbo-core-service-399143297583.asia-southeast1.run.app',
    [EnvironmentTypes.PRODUCTION]:
      'https://askbo-core-service-422880961295.asia-southeast1.run.app',
    [EnvironmentTypes.PRE_PRODUCTION]:
      'https://askbo-core-service-422880961295.asia-southeast1.run.app',
  },
  [AccountEndpoints.ACCOUNT_WEB_BASE_URL]: {
    [EnvironmentTypes.DEVELOPMENT]: 'https://account.devairasia.com',
    [EnvironmentTypes.STAGING]: 'https://account.stgairasia.com',
    [EnvironmentTypes.PRODUCTION]: 'https://account.airasia.com',
    [EnvironmentTypes.PRE_PRODUCTION]: 'https://account.airasia.com',
  },
  [AccountEndpoints.CARD_VAULT_API_BASE_URL]: {
    [EnvironmentTypes.DEVELOPMENT]:
      'https://k.stg-apiairasia.com/stgpayment/cardvault',
    [EnvironmentTypes.STAGING]:
      'https://k.stg-apiairasia.com/stgpayment/cardvault',
    [EnvironmentTypes.PRODUCTION]: 'https://k.apiairasia.com/payment/cardvault',
    [EnvironmentTypes.PRE_PRODUCTION]:
      'https://k.stg-apiairasia.com/stgpayment/cardvault',
  },
  [AccountEndpoints.MY_CASES_WIDGET_SCRIPT_URL]: {
    [EnvironmentTypes.DEVELOPMENT]:
      'https://storage.googleapis.com/ava-scripts-stg/mycases/wdgt-my-cases.js',
    [EnvironmentTypes.STAGING]:
      'https://storage.googleapis.com/ava-scripts-stg/mycases/wdgt-my-cases.js',
    [EnvironmentTypes.PRODUCTION]:
      'https://storage.googleapis.com/ava-scripts-prd/mycases/wdgt-my-cases.js',
    [EnvironmentTypes.PRE_PRODUCTION]:
      'https://storage.googleapis.com/ava-scripts-prd/mycases/wdgt-my-cases.js',
  },
};

export const parseEnv = (
  env: string | EnvironmentTypes = EnvironmentTypes.PRODUCTION,
): EnvironmentTypes => {
  const value = env.toString().toLowerCase();

  if (value.includes('dev')) {
    return EnvironmentTypes.DEVELOPMENT;
  }
  if (value.includes('staging') || value.includes('stg')) {
    return EnvironmentTypes.STAGING;
  }
  if (value.includes('prod') || value.includes('prd')) {
    return EnvironmentTypes.PRODUCTION;
  }
  if (value.includes('pp')) {
    return EnvironmentTypes.PRE_PRODUCTION;
  }

  return EnvironmentTypes.PRODUCTION;
};

export default AccountEnvironmentURLs;
